import { addStyles, DefaultTheme, rgba } from '../../../components/theme';
import { BLACK } from '../../../components/theme.palette';
import { m } from '../../../mithril';
import { classNames } from '../../../util/classnames';

const CIRCLE_RADIUS = DefaultTheme.spacingFactor * 0.75;
const CIRCLE_SIZE_PX = CIRCLE_RADIUS * 2 + 'px';
const CIRCLE_BORDER_SIZE = 2;

export const GenericPointCSS = {
	container: 'c-GenericMapPoint',
	isClickable: 'c-GenericMapPoint--clickable',
};

const GenericPointStyles = {
	[GenericPointCSS.container]: {
		height: CIRCLE_SIZE_PX,
		width: CIRCLE_SIZE_PX,
		minWidth: CIRCLE_SIZE_PX,
		maxWidth: CIRCLE_SIZE_PX,
		borderRadius: '50%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		border: `${CIRCLE_BORDER_SIZE}px solid ${rgba(BLACK, 0.5)}`,
		boxShadow: DefaultTheme.shadow.box.dp2,
		transition: ['box-shadow'],
		transitionDuration: 150,
	},
	[`${GenericPointCSS.container}.${GenericPointCSS.isClickable}`]: {
		cursor: 'pointer',
	},
	[`${GenericPointCSS.container}.${GenericPointCSS.isClickable}:hover`]: {
		boxShadow: DefaultTheme.shadow.box.dp2w5,
	},
};

addStyles(GenericPointStyles);

export interface GenericMapPointOptions {
	key: string;
	point: google.maps.Point;
	backgroundColor: string;
	onClick?: (e: Event) => void;
	title?: string;
	additionalClasses?: string;
	additionalStyles?: Record<string, any>;
}

export const GenericMapPoint = {
	view({ key, point, backgroundColor, onClick, title, additionalClasses, additionalStyles }: GenericMapPointOptions) {
		let mouseDownPos: { x: number; y: number } | null = null;
		const DRAG_THRESHOLD = 5; // pixels

		return m('div', {
			key,
			className: classNames(GenericPointCSS.container, additionalClasses, {
				[GenericPointCSS.isClickable]: !!onClick,
			}),
			onmousedown: (e: MouseEvent) => {
				mouseDownPos = { x: e.clientX, y: e.clientY };
			},
			onclick: (e: Event) => {
				if (!onClick) return;

				// Check if this was a drag or a click
				if (mouseDownPos) {
					const mouseEvent = e as MouseEvent;
					const dx = Math.abs(mouseEvent.clientX - mouseDownPos.x);
					const dy = Math.abs(mouseEvent.clientY - mouseDownPos.y);
					const distance = Math.sqrt(dx * dx + dy * dy);

					// If mouse moved more than threshold, it's a drag - don't trigger click
					if (distance > DRAG_THRESHOLD) {
						mouseDownPos = null;
						return;
					}
				}

				mouseDownPos = null;
				onClick(e);
			},
			title,
			style: {
				top: point.y - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
				left: point.x - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
				backgroundColor,
				...additionalStyles,
			},
		});
	},
};
